// ST-1258 -Claude- Product detail page — Amazon-style layout.
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getProduct } from '@nearrme/api-client';
import { PriceBlock, Card, BusinessCard, EmptyState, LoadingSkeleton, Button } from '@nearrme/ui-web';
import { getApiClient } from '../api/client';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const { t } = useTranslation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProduct(getApiClient(), Number(productId)),
    enabled: !!productId,
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <LoadingSkeleton className="aspect-square" />
          <div className="space-y-4">
            <LoadingSkeleton className="h-8 w-3/4" />
            <LoadingSkeleton className="h-6 w-1/2" />
            <LoadingSkeleton className="h-12 w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <EmptyState title={t('errors.notFound')} description="This product might be unavailable." />
      </div>
    );
  }

  const product = data.data;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 animate-fade-in">
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {/* IMAGES */}
        <div className="aspect-square rounded-2xl bg-slate-100 dark:bg-slate-800 overflow-hidden">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <span className="text-6xl">📦</span>
            </div>
          )}
        </div>

        {/* DETAILS */}
        <div>
          {product.brandName && (
            <div className="text-sm text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {product.brandName}
            </div>
          )}
          <h1 className="mt-1 text-3xl font-bold text-slate-900 dark:text-slate-100">
            {product.name}
          </h1>

          {product.description && (
            <p className="mt-4 text-slate-600 dark:text-slate-300">{product.description}</p>
          )}

          <div className="mt-6">
            <PriceBlock
              price={product.price ?? null}
              mrpPrice={product.mrpPrice ?? null}
              discountPercent={product.discountPercent ?? null}
              emiPerMonth={product.emiPerMonth ?? null}
            />
          </div>

          <div className="mt-6 flex items-center gap-3">
            {product.gstInvoiceAvailable && (
              <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400">
                ✓ {t('product.gstAvailable')}
              </span>
            )}
            <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400">
              ✓ Brand-Authorized
            </span>
          </div>

          <div className="mt-8 flex gap-3">
            <Button variant="primary" size="lg" fullWidth>
              {t('product.reserve')}
            </Button>
            <Button variant="outline" size="lg" fullWidth>
              ❤
            </Button>
          </div>
        </div>
      </div>

      {/* AVAILABLE AT */}
      <section className="mt-12">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4">
          {t('product.availableAt')}
        </h2>
        {product.availableAt.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {product.availableAt.map((b) => (
              <BusinessCard
                key={b.businessId}
                business={b}
                onClick={(biz) => (window.location.href = `/b/${biz.businessId}`)}
              />
            ))}
          </div>
        ) : (
          <Card>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              No nearby stores found. Try searching from a different location.
            </p>
          </Card>
        )}
      </section>
    </div>
  );
}
